import { useState, FormEvent, useEffect } from 'react';
import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  fetchUserAttributes,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './Auth.css';
import { useUserContext } from '../Context/UserContext';

/** -------------------------------------------
 * Strict next path validation
 * ------------------------------------------*/
function isSafeNextPath(next?: string | null): boolean {
  if (!next) return false;
  if (!next.startsWith('/')) return false;
  const lower = next.toLowerCase();

  // Block auth routes
  if (
    lower.startsWith('/login') ||
    lower.startsWith('/register') ||
    lower.startsWith('/forgot-password')
  ) {
    return false;
  }

  // Whitelist simple routes
  if (lower === '/' || lower === '/home') return true;

  // Whitelist param routes with an id
  if (/^\/session\/[a-z0-9_-]+$/i.test(lower)) return true; // session detail
  if (/^\/coll\/[a-z0-9_-]+$/i.test(lower)) return true;    // collaborator invite

  // treat list page "/session" as NOT safe for post-register redirect
  if (lower === '/session') return false;

  return false;
}
function sanitizeNext(next?: string | null): string {
  return isSafeNextPath(next) ? (next as string) : '/';
}

/** Debug helper: decode a JWT (ID token) body safely */
const decodeJwt = (t: string) => {
  try { return JSON.parse(atob(t.split('.')[1])); } catch { return {}; }
};

export default function RegisterComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useUserContext();

  const [step, setStep] = useState<'register' | 'confirm'>('register');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Intended destination (query first, then state, then localStorage fallback)
  const qp = new URLSearchParams(location.search);
  const nextFromQuery = qp.get('next') || '';
  const nextFromStateNext = (location.state as any)?.next || '';
  const nextFromStateFrom = (location.state as any)?.from?.pathname || '';
  const lsNext = ((): string => {
    try {
      return localStorage.getItem('nextAfterAuth') || '';
    } catch {
      return '';
    }
  })();
  const rawNext = nextFromQuery || nextFromStateNext || nextFromStateFrom || lsNext || '';
  const next = sanitizeNext(rawNext);

  // Prefill email from localStorage (manual email capture), if present
  useEffect(() => {
    try {
      const storedEmail = localStorage.getItem('manualEmail') || '';
      if (storedEmail) setEmail(storedEmail);
    } catch { /* ignore */ }
  }, []);

  function getFriendlyError(err: any): string {
    if (!err?.name) return err?.message || 'An unknown error occurred';
    switch (err.name) {
      case 'UsernameExistsException':
        return 'An account with this email already exists.';
      case 'CodeMismatchException':
        return 'The confirmation code you entered is incorrect.';
      case 'ExpiredCodeException':
        return 'The confirmation code has expired. Please request a new one.';
      case 'InvalidPasswordException':
        return 'Password must meet complexity requirements.';
      default:
        return err.message || 'An error occurred.';
    }
  }

  async function handleRegister(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setMessage(null);
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    console.log('[SIGNUP] Submitting attributes:', {
      email: trimmedEmail,
      given_name: trimmedFirst,
      family_name: trimmedLast,
    });

    try {
      await signUp({
        username: trimmedEmail,
        password,
        options: {
          userAttributes: {
            email: trimmedEmail,
            given_name: trimmedFirst,
            family_name: trimmedLast,
          },
        },
      });
      setStep('confirm');
    } catch (err: any) {
      setError(getFriendlyError(err));
    }
  }

  async function handleConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    try {
      await confirmSignUp({ username: email, confirmationCode });
      await signIn({ username: email, password });

      // Hydrate and LOG everything — no fallbacks, no silent fixes
      let attrs: Record<string, string> = {};
      for (let i = 0; i < 6; i++) {
        try {
          attrs = (await fetchUserAttributes()) as Record<string, string>;
          break;
        } catch {
          await new Promise((r) => setTimeout(r, 150));
        }
      }
      console.log('[AUTH] fetchUserAttributes:', attrs);

      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const idt = session.tokens?.idToken?.toString();
        if (idt) {
          const claims = decodeJwt(idt);
          console.log('[AUTH] ID token claims:', claims, {
            has_given_name: !!(claims as any)?.given_name,
            has_family_name: !!(claims as any)?.family_name,
          });
        } else {
          console.warn('[AUTH] No ID token present');
        }
      } catch (e) {
        console.warn('[AUTH] fetchAuthSession failed:', e);
      }

      await refreshUser();
      await new Promise((r) => setTimeout(r, 120));

      // Redirect logic unchanged
      let target = next;

      if (!isSafeNextPath(target)) {
        let pendingSessionId = '';
        let registerSource = '';
        try {
          pendingSessionId = localStorage.getItem('pendingSessionId') || '';
          registerSource = localStorage.getItem('registerSource') || '';
        } catch {}

        if (pendingSessionId && registerSource === 'initiator') {
          target = `/session/${pendingSessionId}`;
        } else if (pendingSessionId && registerSource === 'collaborator') {
          target = `/coll/${pendingSessionId}`;
        } else if (isSafeNextPath(lsNext)) {
          target = lsNext;
        } else {
          target = '/';
        }
      }

      // Cleanup a few temp values
      try {
        localStorage.removeItem('manualEmail');
        localStorage.removeItem('pendingSessionId');
        localStorage.removeItem('registerSource');
        localStorage.removeItem('nextAfterAuth');
      } catch {}

      navigate(target, { replace: true });
    } catch (err: any) {
      console.error('❌ Confirmation error:', err);
      setError(getFriendlyError(err));
    }
  }

  async function handleResendCode() {
    setError(null);
    setMessage(null);
    try {
      await resendSignUpCode({ username: email });
      setMessage('Confirmation code resent! Please check your email.');
    } catch (err: any) {
      setError(getFriendlyError(err));
    }
  }

  return (
    <div className="auth-container">
      <h2>{step === 'register' ? 'Register' : 'Confirm Your Email'}</h2>

      {step === 'register' ? (
        <form onSubmit={handleRegister}>
          <input
            name="firstName"
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <input
            name="lastName"
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            name="password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit">Register</button>
        </form>
      ) : (
        <form onSubmit={handleConfirm}>
          <p>
            A confirmation code was sent to <strong>{email}</strong>. Enter it below.
          </p>
          <input
            type="text"
            placeholder="Confirmation code"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            required
          />
          <button type="submit">Confirm</button>
          <button type="button" onClick={handleResendCode} style={{ marginTop: '1rem' }}>
            Resend Code
          </button>
        </form>
      )}

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <div className="auth-footer">
        <Link to={isSafeNextPath(rawNext) ? `/login?next=${encodeURIComponent(rawNext)}` : '/login'}>
          Back to login
        </Link>
      </div>
    </div>
  );
}
